/**
 * Skills Registry
 *
 * Dynamic registry of skills/tools with category coupling for the autonomy tab.
 * Each skill defines:
 * - Category (communication, productivity, data, etc.)
 * - Actions (read, write, send, etc.)
 * - Policy defaults
 */

export type SkillCategory =
  | "communication"
  | "productivity"
  | "data"
  | "automation"
  | "integration"
  | "development"
  | "security"
  | "other";

export type ActionType = "check" | "do";

export interface SkillAction {
  key: string; // e.g., "sendblue.read", "sendblue.send"
  label: string;
  description: string;
  actionType: ActionType;
  /** Default policy for this action */
  defaultPolicy?:
    | "ask_first"
    | "on_request"
    | "always_check"
    | "proactive_notify"
    | "allow"
    | "deny";
}

export interface SkillDefinition {
  id: string;
  name: string;
  icon: string;
  category: SkillCategory;
  description: string;
  actions: SkillAction[];
  /** Is this a core/system skill vs user-added? */
  isCore?: boolean;
  /** External service URL */
  serviceUrl?: string;
  /** Docs URL */
  docsUrl?: string;
}

/**
 * Core skills registry - these are the built-in skills
 */
export const CORE_SKILLS: SkillDefinition[] = [
  // Communication category
  {
    id: "sendblue",
    name: "Sendblue (iMessage)",
    icon: "💬",
    category: "communication",
    description: "Send and receive iMessages via Sendblue",
    serviceUrl: "https://sendblue.co",
    actions: [
      {
        key: "sendblue.read",
        label: "Read texts",
        description: "Read incoming iMessages and check message status",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "sendblue.send",
        label: "Send texts",
        description: "Send iMessages on your behalf",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
      {
        key: "sendblue.group",
        label: "Group messages",
        description: "Send messages to group chats",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
    ],
  },
  {
    id: "email",
    name: "Email",
    icon: "📧",
    category: "communication",
    description: "Read and send emails",
    actions: [
      {
        key: "email.read",
        label: "Read email",
        description: "Read inbox, search, and summarize emails",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "email.send",
        label: "Send email",
        description: "Send emails on your behalf",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
      {
        key: "email.draft",
        label: "Draft email",
        description: "Create email drafts for review",
        actionType: "do",
        defaultPolicy: "on_request",
      },
    ],
  },
  {
    id: "slack",
    name: "Slack",
    icon: "💼",
    category: "communication",
    description: "Read and post to Slack channels",
    actions: [
      {
        key: "slack.read",
        label: "Read channels",
        description: "Read channel messages and history",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "slack.post",
        label: "Post messages",
        description: "Post messages to channels",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
      {
        key: "slack.dm",
        label: "Direct messages",
        description: "Send direct messages",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
    ],
  },
  {
    id: "discord",
    name: "Discord",
    icon: "🎮",
    category: "communication",
    description: "Read and post to Discord servers",
    actions: [
      {
        key: "discord.read",
        label: "Read channels",
        description: "Read server messages",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "discord.post",
        label: "Post messages",
        description: "Post to Discord channels",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
    ],
  },

  // Productivity category
  {
    id: "calendar",
    name: "Calendar",
    icon: "📅",
    category: "productivity",
    description: "View and manage calendar events",
    actions: [
      {
        key: "calendar.read",
        label: "View calendar",
        description: "View events and check availability",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "calendar.create",
        label: "Create events",
        description: "Create new calendar events",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
      {
        key: "calendar.modify",
        label: "Modify events",
        description: "Edit or delete existing events",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
    ],
  },
  {
    id: "notion",
    name: "Notion",
    icon: "📝",
    category: "productivity",
    description: "Read and update Notion pages",
    actions: [
      {
        key: "notion.read",
        label: "Read pages",
        description: "Read Notion pages and databases",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "notion.write",
        label: "Update pages",
        description: "Create and edit Notion pages",
        actionType: "do",
        defaultPolicy: "on_request",
      },
    ],
  },
  {
    id: "documents",
    name: "Documents",
    icon: "📄",
    category: "productivity",
    description: "Generate PowerPoint, Word, and Excel documents",
    actions: [
      {
        key: "documents.generate",
        label: "Generate documents",
        description: "Create PPTX, DOCX, XLSX files",
        actionType: "do",
        defaultPolicy: "on_request",
      },
      {
        key: "documents.deliver",
        label: "Deliver documents",
        description: "Send documents via email or download link",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
    ],
  },

  // Development category
  {
    id: "github",
    name: "GitHub",
    icon: "🐙",
    category: "development",
    description: "Read and manage GitHub repositories",
    actions: [
      {
        key: "github.read",
        label: "Read repositories",
        description: "View code, issues, and PRs",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "github.write",
        label: "Write to repositories",
        description: "Create issues, PRs, and commits",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
      {
        key: "github.actions",
        label: "GitHub Actions",
        description: "Trigger and manage workflows",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
    ],
  },
  {
    id: "code",
    name: "Code Execution",
    icon: "💻",
    category: "development",
    description: "Execute code and shell commands",
    isCore: true,
    actions: [
      {
        key: "code.read",
        label: "Read files",
        description: "Read source code and files",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "code.write",
        label: "Write files",
        description: "Create and edit source code",
        actionType: "do",
        defaultPolicy: "on_request",
      },
      {
        key: "code.execute",
        label: "Execute commands",
        description: "Run shell commands and scripts",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
    ],
  },

  // Data category
  {
    id: "web",
    name: "Web Search",
    icon: "🔍",
    category: "data",
    description: "Search the web and fetch pages",
    isCore: true,
    actions: [
      {
        key: "web.search",
        label: "Web search",
        description: "Search the web for information",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "web.fetch",
        label: "Fetch pages",
        description: "Retrieve webpage content",
        actionType: "check",
        defaultPolicy: "always_check",
      },
    ],
  },
  {
    id: "memory",
    name: "Memory",
    icon: "🧠",
    category: "data",
    description: "Remember things across conversations",
    isCore: true,
    actions: [
      {
        key: "memory.read",
        label: "Recall memories",
        description: "Read saved memories",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "memory.write",
        label: "Save memories",
        description: "Store new information",
        actionType: "do",
        defaultPolicy: "on_request",
      },
    ],
  },

  // Automation category
  {
    id: "personas",
    name: "Personas/Delegation",
    icon: "👥",
    category: "automation",
    description: "Delegate tasks to specialized personas",
    isCore: true,
    actions: [
      {
        key: "personas.spawn",
        label: "Spawn personas",
        description: "Start specialized agent sessions",
        actionType: "do",
        defaultPolicy: "on_request",
      },
      {
        key: "personas.send",
        label: "Message personas",
        description: "Send tasks to running personas",
        actionType: "do",
        defaultPolicy: "on_request",
      },
    ],
  },
  {
    id: "automation",
    name: "Automation Rules",
    icon: "⚙️",
    category: "automation",
    description: "Create and manage automation rules",
    actions: [
      {
        key: "automation.read",
        label: "View rules",
        description: "View automation rules and history",
        actionType: "check",
        defaultPolicy: "always_check",
      },
      {
        key: "automation.create",
        label: "Create rules",
        description: "Create new automation rules",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
      {
        key: "automation.execute",
        label: "Execute rules",
        description: "Run automations manually",
        actionType: "do",
        defaultPolicy: "ask_first",
      },
    ],
  },
];

/**
 * Get all skills grouped by category
 */
export function getSkillsByCategory(): Record<SkillCategory, SkillDefinition[]> {
  const grouped: Record<SkillCategory, SkillDefinition[]> = {
    communication: [],
    productivity: [],
    data: [],
    automation: [],
    integration: [],
    development: [],
    security: [],
    other: [],
  };

  for (const skill of CORE_SKILLS) {
    grouped[skill.category].push(skill);
  }

  return grouped;
}

/**
 * Get all actions as a flat list for autonomy settings
 */
export function getAllActions(): Array<{ skill: SkillDefinition; action: SkillAction }> {
  const actions: Array<{ skill: SkillDefinition; action: SkillAction }> = [];
  for (const skill of CORE_SKILLS) {
    for (const action of skill.actions) {
      actions.push({ skill, action });
    }
  }
  return actions;
}

/**
 * Get common tasks for autonomy settings UI (backwards compatible format)
 */
export function getCommonTasks(): Array<{
  key: string;
  label: string;
  category: string;
  description?: string;
}> {
  const tasks: Array<{ key: string; label: string; category: string; description?: string }> = [];
  for (const skill of CORE_SKILLS) {
    for (const action of skill.actions) {
      tasks.push({
        key: action.key,
        label: action.label,
        category: skill.name,
        description: action.description,
      });
    }
  }
  return tasks;
}

/**
 * Get category display info
 */
export const CATEGORY_INFO: Record<SkillCategory, { label: string; icon: string }> = {
  communication: { label: "Communication", icon: "💬" },
  productivity: { label: "Productivity", icon: "📋" },
  data: { label: "Data & Search", icon: "🔍" },
  automation: { label: "Automation", icon: "⚙️" },
  integration: { label: "Integrations", icon: "🔗" },
  development: { label: "Development", icon: "💻" },
  security: { label: "Security", icon: "🔒" },
  other: { label: "Other", icon: "📦" },
};

/**
 * Find a skill by ID
 */
export function findSkillById(id: string): SkillDefinition | undefined {
  return CORE_SKILLS.find((s) => s.id === id);
}

/**
 * Find an action by key
 */
export function findActionByKey(
  key: string,
): { skill: SkillDefinition; action: SkillAction } | undefined {
  for (const skill of CORE_SKILLS) {
    for (const action of skill.actions) {
      if (action.key === key) {
        return { skill, action };
      }
    }
  }
  return undefined;
}
